import '../css/app.css';

import { createInertiaApp, router } from '@inertiajs/react';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createRoot } from 'react-dom/client';
import { initializeTheme } from './hooks/use-appearance';

const appName = import.meta.env.VITE_APP_NAME || 'Laravel';
const gaId = import.meta.env.VITE_GA_ID as string | undefined;
const consentStorageKey = 'cookie_consent';

declare global {
    interface Window {
        dataLayer?: unknown[];
        gtag?: (...args: unknown[]) => void;
        enableAnalytics?: () => void;
    }
}


createInertiaApp({
    title: (title) => `${appName} - ${title}`,
    resolve: (name) => resolvePageComponent(`./pages/${name}.tsx`, import.meta.glob('./pages/**/*.tsx')),
    setup({ el, App, props }) {
        const root = createRoot(el);

        root.render(<App {...props} />);
    },
    progress: {
        color: '#d5e40a',
    },
});

if (gaId) {
    let lastPath: string | null = null;
    let analyticsAllowed = false;
    let lastConsentRaw: string | null = null;

    const shouldTrack = (url: string) => !url.startsWith('/admin');

    const readAnalyticsConsent = (): boolean => {
        const raw = window.localStorage.getItem(consentStorageKey);

        if (raw === lastConsentRaw) {
            return analyticsAllowed;
        }

        lastConsentRaw = raw;

        if (!raw) {
            return false;
        }

        try {
            const parsed = JSON.parse(raw) as { analytics?: boolean };
            return parsed.analytics === true;
        } catch {
            return false;
        }
    };

    const trackPageView = (url: string) => {
        if (!analyticsAllowed || !window.gtag || !shouldTrack(url) || lastPath === url) {
            return;
        }

        lastPath = url;

        const absoluteUrl = new URL(url, window.location.origin);
        window.gtag('event', 'page_view', {
            page_location: absoluteUrl.href,
            page_path: `${absoluteUrl.pathname}${absoluteUrl.search}${absoluteUrl.hash}`,
            page_title: document.title,
        });
    };

    const trackCurrentPage = () => {
        trackPageView(`${window.location.pathname}${window.location.search}${window.location.hash}`);
    };

    const syncConsent = () => {
        const allowed = readAnalyticsConsent();

        if (allowed === analyticsAllowed) {
            return;
        }

        analyticsAllowed = allowed;

        if (analyticsAllowed) {
            trackCurrentPage();
        }
    };

    window.enableAnalytics = syncConsent;
    syncConsent();

    router.on('navigate', (event) => {
        trackPageView(event.detail.page.url);
    });

    const handleConsentUpdated = () => {
        syncConsent();
    };

    const handleStorage = (event: StorageEvent) => {
        if (event.key === consentStorageKey) {
            syncConsent();
        }
    };

    const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            syncConsent();
        }
    };

    window.addEventListener('cookie_consent_updated', handleConsentUpdated as EventListener);
    window.addEventListener('storage', handleStorage);
    document.addEventListener('visibilitychange', handleVisibilityChange);
}

// This will set light / dark mode on load...
initializeTheme();
